import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { environment } from '@environment';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { SpaceParticipantsService } from '@modules/settings/services/space-participants.service';
import type { Space } from '@modules/settings/types/space';
import type { NoteCollaborationDto, NoteTeamAccess } from '@sharedlib/dtos/dto/notes-app/note-collaboration.dto';
import type { TeamOwnedNotesDto } from '@sharedlib/dtos/dto/team-note-access.dto';
import { defer, EMPTY, expand, map, reduce, throwError, type Observable } from 'rxjs';

export const TEAM_NOTE_ACCESS_TOAST = 'Access saved. Team membership may still provide access.';
export function formerNoteOwnerName(names: ReadonlyMap<string, string>, id: string): string {
  return names.get(id)?.trim() || 'Former member';
}
/** Converts validation arrays and refusal messages to text, without interpreting server HTML. */
export function teamNoteAccessError(error: unknown, fallback = 'Could not change team access. Retry.'): string {
  if (error && typeof error === 'object') {
    const detail = Reflect.get(error, 'error');
    if (detail && typeof detail === 'object') {
      const message = Reflect.get(detail, 'message');
      if (typeof message === 'string' && message) return message;
      if (Array.isArray(message)) {
        const text = message.filter((part): part is string => typeof part === 'string').join(' ');
        if (text) return text;
      }
    }
  }
  return fallback;
}
@Injectable()
export class TeamNoteAccessClient {
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  public listOwnedReadOnly(
    team: string,
    query: { ownerAccountId?: string; offset: number; limit: number },
  ): Observable<TeamOwnedNotesDto> {
    return this.http.get<TeamOwnedNotesDto>(`${environment.baseUrl}/api/team/notes/owned-read-only`, {
      context: owningTenantContext(team),
      params: {
        ...(query.ownerAccountId ? { ownerAccountId: query.ownerAccountId } : {}),
        offset: query.offset,
        limit: query.limit,
      },
    });
  }
  public setTeamAccess(
    team: string,
    noteId: string,
    access: NoteTeamAccess,
    expectedRevision: number,
  ): Observable<{ collaboration: NoteCollaborationDto }> {
    return this.http.put<{ collaboration: NoteCollaborationDto }>(
      `${environment.baseUrl}/api/team/notes/${noteId}/team-access`,
      { access, expectedRevision },
      { context: owningTenantContext(team) },
    );
  }
  public formerOwners(space: Space): Observable<ReadonlyMap<string, string>> {
    // Notes only use the access routes; resolve the names service only on the Settings list.
    const participants = this.injector.get(SpaceParticipantsService);
    const read = (offset: number) =>
      participants
        .listMembers(space, { tab: 'removed', search: '', role: '', limit: 100, offset })
        .pipe(map((result) => ({ result, offset })));
    return defer(() => read(0)).pipe(
      expand(({ result, offset }) => {
        const next = offset + result.items.length;
        if (next >= (result.counts.removed ?? 0)) return EMPTY;
        if (!result.items.length)
          return throwError(() => new Error('The former member list could not be read completely.'));
        return read(next);
      }),
      reduce((names: Map<string, string>, { result }) => {
        for (const row of result.items) if (row.kind === 'removed') names.set(row.id, row.name);
        return names;
      }, new Map<string, string>()),
    );
  }
}
